import { type ComponentType } from 'react';
import { defineMessages, type MessageDescriptor, useIntl } from 'react-intl';
import classNames from 'classnames';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { TenantEnum } from '@jsmdg/tenancy';
import {
    CheckCircleIcon,
    GiftWrappingIcon,
    type IconComponentProps,
    PdfIcon,
    PureLink,
} from '@jsmdg/yoshi';
import { ProductType } from '../../../../shared/enums';
import { type ProductGroupItem } from '../../../../shared/types';
import { DeliveryFormatTrackingLabel } from '../../../types/enums/deliveryFormatTrackingLabel';
import { type ProductDetailContext } from '../../../types/types';
import { trackDeliveryFormatClicked } from './deliveryFormatGroup.tracking';
import styles from './DeliveryFormatGroup.module.scss';

const messages = defineMessages({
    a11yGroupLabel: {
        defaultMessage: 'Lieferformat',
    },
    giftBoxTitle: {
        defaultMessage: 'Geschenkbox',
    },
    giftBoxHintDE: {
        defaultMessage: 'Standardlieferzeit 1-2 Werktage | Expresslieferung möglich',
    },
    giftBoxHintATAndCH: {
        defaultMessage: 'Lieferzeit 5-6 Werktage',
    },
    pdfVoucherTitle: {
        defaultMessage: 'PDF-Gutschein',
    },
    pdfVoucherHint: {
        defaultMessage: 'Sofort per E-Mail erhältlich',
    },
});

const giftBoxHintForTenants: { [key: string]: MessageDescriptor } = {
    [TenantEnum.JS_DE]: messages.giftBoxHintDE,
    [TenantEnum.JS_AT]: messages.giftBoxHintATAndCH,
    [TenantEnum.MD_DE]: messages.giftBoxHintDE,
    [TenantEnum.MD_AT]: messages.giftBoxHintATAndCH,
    [TenantEnum.MD_CH]: messages.giftBoxHintATAndCH,
};

type DeliveryFormatGroupProps = {
    readonly items: ProductGroupItem[];
    readonly selectedType?: ProductType;
};

type DeliveryFormatOption = {
    item: ProductGroupItem;
    trackingLabel: DeliveryFormatTrackingLabel;
    icon: ComponentType<IconComponentProps>;
    title: MessageDescriptor;
    hint: MessageDescriptor;
};

const DeliveryFormatGroup = ({
    items,
    selectedType,
}: DeliveryFormatGroupProps): JSX.Element | null => {
    const intl = useIntl();
    const { tenant } = useFragmentContext<ProductDetailContext>();

    const giftBoxItem = items.find(item => item.type === ProductType.BOX);
    const pdfVoucherItem = items.find(item => item.type === ProductType.EXPERIENCE);

    if (!giftBoxItem || !pdfVoucherItem) {
        return null;
    }

    const options: DeliveryFormatOption[] = [
        {
            item: giftBoxItem,
            trackingLabel: DeliveryFormatTrackingLabel.GiftBox,
            icon: GiftWrappingIcon,
            title: messages.giftBoxTitle,
            hint: giftBoxHintForTenants[tenant] ?? messages.giftBoxHintDE,
        },
        {
            item: pdfVoucherItem,
            trackingLabel: DeliveryFormatTrackingLabel.PdfVoucher,
            icon: PdfIcon,
            title: messages.pdfVoucherTitle,
            hint: messages.pdfVoucherHint,
        },
    ];

    return (
        <div
            aria-label={intl.formatMessage(messages.a11yGroupLabel)}
            className={classNames('d-grid gap-2x', styles.optionList)}
            data-testid="delivery-format-group"
        >
            {options.map(option => {
                const isSelected = option.item.type === selectedType;
                const Icon = option.icon;

                return (
                    <PureLink
                        key={option.item.productId}
                        href={option.item.url}
                        aria-current={isSelected ? 'page' : undefined}
                        className={classNames(
                            'position-relative d-flex flex-column gap-0-5x pt-2x px-2x pb-3x text-start text-decoration-none bg-white-0',
                            styles.option,
                            { [styles.optionSelected]: isSelected },
                        )}
                        data-testid={`delivery-format-option-${option.trackingLabel}`}
                        onClick={() => {
                            void trackDeliveryFormatClicked(option.trackingLabel);
                        }}
                    >
                        <span className="d-flex align-items-center gap-1x">
                            <Icon
                                size={24}
                                className={classNames('flex-shrink-0', styles.optionIcon, {
                                    [styles.optionIconSelected]: isSelected,
                                })}
                            />
                            <span
                                className={classNames(
                                    'font-complementary fw-bold',
                                    styles.optionTitle,
                                )}
                            >
                                {intl.formatMessage(option.title)}
                            </span>
                        </span>
                        <span className={styles.optionHint}>{intl.formatMessage(option.hint)}</span>
                        {isSelected && (
                            <span
                                className={classNames(
                                    'position-absolute d-flex align-items-center justify-content-center',
                                    styles.selectedBadge,
                                )}
                                aria-hidden="true"
                            >
                                <CheckCircleIcon size={24} />
                            </span>
                        )}
                    </PureLink>
                );
            })}
        </div>
    );
};

export { DeliveryFormatGroup };
